/* Codex. One delegated listener set; per-section observers are cleaned up in Shopify's editor. */
(() => {
  if (window.bsOfficialReady) return;
  window.bsOfficialReady = true;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const cleanups = new Map();
  const scriptElement = document.currentScript || document.querySelector('script[data-bs-math-src]');
  const mathUrl = scriptElement?.dataset.bsMathSrc || new URL('bs-savings.js', scriptElement?.src || document.baseURI).href;
  const genreUrl = scriptElement?.dataset.bsGenreSrc || new URL('bs-genres.js', scriptElement?.src || document.baseURI).href;
  let genreModule;
  let mathModule;
  const money = cents => new Intl.NumberFormat('en-US', { style:'currency', currency:'USD' }).format(cents / 100);
  const collect = (root, selector) => [...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector)];
  const register = (element, cleanup) => cleanups.set(element, cleanup);

  async function initCalculator(root) {
    if (cleanups.has(root)) return;
    const controller = new AbortController();
    register(root, () => controller.abort());
    let calculateSavings;
    try {
      mathModule ??= import(mathUrl);
      ({ calculateSavings } = await mathModule);
    } catch {
      root.querySelector('[data-bs-result]').hidden = true;
      const message = root.querySelector('[data-bs-calculator-unavailable]');
      message.hidden = false;
      message.textContent = 'The interactive estimate is unavailable. See the product page for current box options.';
      root.querySelectorAll('input, button').forEach(input => { input.disabled = true; });
      return;
    }
    if (controller.signal.aborted) return;
    const range = root.querySelector('[data-bs-baseline]');
    const link = root.querySelector('[data-bs-box-link]');
    const output = (name, value) => root.querySelectorAll(`[data-bs-${name}]`).forEach(el => { el.textContent = value; });
    function update() {
      const chosen = root.querySelector('[data-bs-size]:checked');
      const baseline = Number(range.value);
      range.style.setProperty('--bs-range-fill', `${(baseline - 12) / 13 * 100}%`);
      range.setAttribute('aria-valuetext', `${baseline} US dollars per book`);
      output('usual-price', money(baseline * 100));
      root.querySelectorAll('[data-bs-baseline-step]').forEach(button => {
        button.disabled = range.disabled || (Number(button.dataset.bsBaselineStep) < 0 ? baseline <= 12 : baseline >= 25);
      });
      let result;
      try {
        if (!chosen || chosen.disabled || root.dataset.bsCurrency !== 'USD') throw new Error('Price unavailable');
        result = calculateSavings(Number(chosen.value), baseline, Number(chosen.dataset.price));
      } catch {
        root.querySelector('[data-bs-result]').hidden = true;
        root.querySelector('[data-bs-calculator-unavailable]').hidden = false;
        link.href = link.dataset.bsBaseUrl;
        return;
      }
      root.querySelector('[data-bs-result]').hidden = false;
      root.querySelector('[data-bs-calculator-unavailable]').hidden = true;
      output('selected-size', result.size);
      for (const [name, key] of Object.entries({
        'usual-total':'usualTotalCents', 'first-price':'firstPriceCents', 'total-saved':'totalSavedCents',
        'book-saved':'bookSavedCents', 'book-price':'bookPriceCents'
      })) output(name, money(result[key]));
      const destination = new URL(link.dataset.bsBaseUrl, window.location.origin);
      destination.searchParams.set('variant', chosen.dataset.variant);
      destination.searchParams.set('selling_plan', root.dataset.bsPlan);
      link.href = destination.href;
    }
    root.addEventListener('input', update, {signal:controller.signal});
    root.addEventListener('change', update, {signal:controller.signal});
    root.addEventListener('click', event => {
      const button = event.target.closest('[data-bs-baseline-step]');
      if (!button || button.disabled) return;
      range.value = String(Math.max(12, Math.min(25, Number(range.value) + Number(button.dataset.bsBaselineStep))));
      update();
    }, {signal:controller.signal});
    root.querySelector('.bs-size-options').disabled = false;
    range.disabled = root.dataset.bsCurrency !== 'USD';
    update();
  }

  function initMarquee(root) {
    if (cleanups.has(root)) return;
    const controller = new AbortController();
    const viewport = root.querySelector('.bs-marquee-viewport');
    const track = root.querySelector('.bs-marquee-track');
    const button = root.querySelector('[data-bs-scroll-toggle]');
    const label = root.getAttribute('aria-label');
    let paused = false;
    function sync() {
      const motionOff = reducedMotion.matches || navigator.connection?.saveData;
      root.dataset.bsMotion = motionOff || paused ? 'paused' : 'running';
      button.hidden = !!motionOff;
      button.setAttribute('aria-pressed', String(paused));
      button.setAttribute('aria-label', `${paused ? 'Resume' : 'Pause'} ${label}`);

      button.querySelector('[data-bs-scroll-icon]').textContent = paused ? '▶' : 'Ⅱ';
    }
    button.addEventListener('click', () => {
      // Keep the currently visible excerpt in view when turning animation into manual scrolling.
      const transform = getComputedStyle(track).transform;
      const offset = transform === 'none' ? 0 : Math.abs(new DOMMatrixReadOnly(transform).m41);
      paused = !paused;
      sync();
      viewport.scrollLeft = paused ? offset : 0;
    }, {signal:controller.signal});
    const sizeLoop = () => {
      const width = root.querySelector('.bs-marquee-list').scrollWidth;
      const speed = Number(root.dataset.bsSpeed) || 100;
      if(width > 0) root.style.setProperty('--bs-scroll-duration', `${width / speed}s`);
    };
    const resize = 'ResizeObserver' in window ? new ResizeObserver(sizeLoop) : null;
    resize?.observe(viewport);
    document.fonts?.ready.then(() => { if(!controller.signal.aborted) sizeLoop(); });
    const onVisibility = () => { root.dataset.bsTabHidden = String(document.hidden); };
    document.addEventListener('visibilitychange', onVisibility, {signal:controller.signal});
    reducedMotion.addEventListener('change', sync, {signal:controller.signal});
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
      root.dataset.bsOffscreen = String(!entries[0].isIntersecting);
    }, {rootMargin:'80px'}) : null;
    observer?.observe(root);
    register(root, () => { controller.abort(); observer?.disconnect(); resize?.disconnect(); });
    sizeLoop();
    onVisibility();
    sync();
  }

  function initStories(root) {
    if (cleanups.has(root)) return;
    const controller = new AbortController();
    const grid = root.querySelector('[data-bs-story-grid]');
    const nav = root.querySelector('[data-bs-story-nav]');
    const previous = root.querySelector('[data-bs-story-prev]');
    const next = root.querySelector('[data-bs-story-next]');
    const update = () => {
      const overflow = grid.scrollWidth - grid.clientWidth;
      nav.hidden = overflow < 4;
      previous.disabled = grid.scrollLeft <= 2;
      next.disabled = grid.scrollLeft >= overflow - 2;
    };
    nav.addEventListener('click', event => {
      const button = event.target.closest('button');
      if (!button || button.disabled) return;
      const card = grid.querySelector('.bs-story-card');
      const distance = card.getBoundingClientRect().width + parseFloat(getComputedStyle(grid).columnGap || '0');
      grid.scrollBy({left:button === next ? distance : -distance,behavior:reducedMotion.matches ? 'instant' : 'smooth'});
    }, {signal:controller.signal});
    grid.addEventListener('scroll', update, {passive:true,signal:controller.signal});
    // Only the customer-selected clip plays; pause other clips and videos that leave the story rail.
    root.querySelectorAll('video').forEach(video => video.addEventListener('play', () => {
      root.querySelectorAll('video').forEach(other => { if(other !== video) other.pause(); });
    }, {signal:controller.signal}));
    const videos = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
      entries.forEach(entry => { if (!entry.isIntersecting) entry.target.pause(); });
    }, {threshold:0.15}) : null;
    root.querySelectorAll('video').forEach(video => videos?.observe(video));
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) root.querySelectorAll('video').forEach(video => video.pause());
    }, {signal:controller.signal});
    const observer = 'ResizeObserver' in window ? new ResizeObserver(update) : null;
    observer?.observe(grid);
    window.addEventListener('resize', update, {passive:true,signal:controller.signal});
    register(root, () => { controller.abort(); observer?.disconnect(); videos?.disconnect(); root.querySelectorAll('video').forEach(video=>video.pause()); });
    update();
  }

  async function initMixer(root) {
    if (cleanups.has(root)) return;
    const controller = new AbortController();
    register(root, () => controller.abort());
    let data;
    try { genreModule ??= import(genreUrl); data = await genreModule; }
    catch { root.querySelector('[data-bs-mix-help]').textContent = 'Explore these genres when you build your book box.'; return; }
    if(controller.signal.aborted) return;
    const catalog = new Map(data.genres.map(genre => [genre.id, genre.label]));
    const choices = [...root.querySelectorAll('[data-bs-genre]')];
    let selected = [];
    const label = root.querySelector('[data-bs-blend-label]');
    const heading = root.querySelector('[data-bs-blend-heading]');
    const sentence = root.querySelector('[data-bs-blend-sentence]');
    const tags = root.querySelector('[data-bs-blend-tags]');
    const mobile = root.querySelector('[data-bs-mobile-blend]');
    const mobileTags = root.querySelector('[data-bs-mobile-tags]');
    const empty = {label:label.textContent,heading:heading.textContent,sentence:sentence.textContent};
    const reset = root.querySelector('[data-bs-mix-reset]');
    function update() {
      choices.forEach(button => { button.setAttribute('aria-pressed', String(selected.includes(button.dataset.bsGenre))); });
      root.querySelectorAll('[data-bs-chosen]').forEach((slot,index) => {
        slot.textContent = selected[index] ? catalog.get(selected[index]) : index ? 'Your second genre' : 'Your first genre';
        slot.dataset.bsFilled = String(Boolean(selected[index]));
      });
      reset.hidden = !selected.length;
      root.querySelector('[data-bs-mix-jump]').hidden = selected.length !== 2;
      root.dataset.bsMixComplete = String(selected.length === 2);
      mobileTags.replaceChildren();
      mobileTags.hidden = selected.length !== 2;
      tags.replaceChildren();
      tags.hidden = selected.length !== 2;
      if(selected.length === 2) {
        const blend = data.getGenreBlend(...selected);
        root.querySelector('[data-bs-mobile-pair]').textContent = selected.map(id => catalog.get(id)).join(' + ');
        root.querySelector('[data-bs-mobile-heading]').textContent = blend.heading;
        blend.tags.forEach(text => { const li = document.createElement('li'); li.textContent = text; mobileTags.append(li); });
        label.textContent = blend.label;
        heading.textContent = blend.heading;
        sentence.textContent = blend.sentence;
        blend.tags.forEach(text => { const li=document.createElement('li'); li.textContent=text; tags.append(li); });
        root.querySelector('[data-bs-mix-help]').textContent = 'Your blend is ready. Try a new genre to swap your oldest pick.';
      } else {
        root.querySelector('[data-bs-mobile-pair]').textContent = selected.length ? catalog.get(selected[0]) + ' + …' : 'Your reading chemistry';
        root.querySelector('[data-bs-mobile-heading]').textContent = selected.length ? 'Choose one more to reveal your blend.' : 'Pick two favorites. Find a new possibility.';
        label.textContent = empty.label;
        heading.textContent = selected.length ? `Where will ${catalog.get(selected[0]).toLowerCase()} take you?` : empty.heading;
        sentence.textContent = selected.length ? 'Choose a second genre to uncover your reading possibilities.' : empty.sentence;
        root.querySelector('[data-bs-mix-help]').textContent = selected.length ? 'One more favorite. Then comes the plot twist.' : 'Choose two to see what happens when your reading tastes meet.';
      }
    }
    root.addEventListener('click', event => {
      const choice = event.target.closest('[data-bs-genre]');
      if (choice && !choice.disabled) selected = data.toggleGenreSelection(selected,choice.dataset.bsGenre);
      else if (event.target.closest('[data-bs-mix-reset]')) selected = [];
      else return;
      update();
      // Keep the compact result beside the choices on phones, without moving keyboard focus.
      if (selected.length === 2 && event.detail !== 0 && matchMedia('(max-width: 760px)').matches) {
        const bounds = mobile.getBoundingClientRect();
        if (bounds.top < 0 || bounds.bottom > window.innerHeight) {
          mobile.scrollIntoView({block:'nearest', behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
        }
      }
    }, {signal:controller.signal});
    choices.forEach(button => { button.disabled = !catalog.has(button.dataset.bsGenre); });
    update();
  }

  function init(root) {
    collect(root, '[data-bs-mixer]').forEach(initMixer);
    collect(root, '[data-bs-calculator]').forEach(initCalculator);
    collect(root, '[data-bs-marquee]').forEach(initMarquee);
    collect(root, '.bs-stories').forEach(initStories);
  }
  document.addEventListener('click', event => {
    const menuLink = event.target.closest('.bs-mobile-menu a');
    if(menuLink) menuLink.closest('details').open = false;
    const trigger = event.target.closest('[data-bs-open]');
    if (trigger) document.getElementById(trigger.dataset.bsOpen)?.showModal();
    if (event.target.closest('[data-bs-close]')) event.target.closest('dialog')?.close();
    if (event.target instanceof HTMLDialogElement) {
      const r = event.target.getBoundingClientRect();
      if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) event.target.close();
    }
  });
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('.bs-mobile-menu[open]').forEach(menu => { menu.open = false; menu.querySelector('summary').focus(); });
  });
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [element, cleanup] of cleanups) {
      if (element === event.target || event.target.contains(element)) { cleanup(); cleanups.delete(element); }
    }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), {once:true});
  else init(document);
})();
